import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

function useInView(ref: React.RefObject<HTMLElement | null>) {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setInView(true); }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [ref]);
  return inView;
}

export default function ContactPage() {
  const ref = useRef<HTMLElement>(null);
  const inView = useInView(ref);
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const textarea = form.querySelector('textarea[name="message"]') as HTMLTextAreaElement;
    if (textarea && textarea.value.length > 500) return;
    setStatus('sending');
    try {
      const data = new URLSearchParams();
      Array.from(form.elements).forEach((el) => {
        const input = el as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
        if (input.name && input.value) data.append(input.name, input.value);
      });
      const res = await fetch('https://readdy.ai/api/form/d717dbeuimmp87kg1blg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) { setStatus('success'); form.reset(); setCharCount(0); }
      else setStatus('error');
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative pt-32 pb-16 bg-white overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-teal-50/80 blur-3xl -translate-y-1/4 translate-x-1/4" />
          </div>
          <div className="relative max-w-7xl mx-auto px-6 lg:px-10 text-center">
            {/* <span className="inline-block bg-teal-100 text-teal-700 text-xs font-semibold tracking-widest uppercase px-4 py-2 rounded-full mb-5">
              Contact Us
            </span> */}
            <h1 className="text-5xl lg:text-6xl font-extrabold text-gray-900 leading-tight mb-5">
              Let&apos;s <span className="text-teal-500">Build</span> Together
            </h1>
            <p className="text-gray-500 text-xl max-w-2xl mx-auto">
              25 years of experience. ISO-certified quality. Ready to transform your business — get a free consultation today.
            </p>
          </div>
        </section>

        {/* Contact Section */}
        <section ref={ref} className="pb-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="grid lg:grid-cols-5 gap-12 items-start">

              {/* Left Info */}
              <div className={`lg:col-span-2 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                <h2 className="text-3xl font-extrabold text-gray-900 mb-5">Get in Touch</h2>
                <p className="text-gray-500 leading-relaxed mb-8">
                  Our team of certified IT architects and software engineers is ready to discuss your project. Fill out the form and we&apos;ll get back to you within 24 hours.
                </p>

                {/* Contact Details */}
                <div className="space-y-5 mb-8">
                  {[
                    { icon: 'ri-map-pin-line', label: 'Office Address', value: '401 Shukh Complex,Munsipuliya\n Indira Nagar,Lucknow,226016' },
                    { icon: 'ri-phone-line', label: 'Phone', value: '+91 8853997700' },
                    { icon: 'ri-mail-line', label: 'Email', value: 'info@elationsoft.net' },
                    { icon: 'ri-time-line', label: 'Business Hours', value: 'Mon – Fri: 9:00 AM – 6:00 PM (EST)' },
                  ].map((item) => (
                    <div key={item.label} className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center bg-teal-100 text-teal-600 rounded-xl flex-shrink-0">
                        <i className={`${item.icon} text-xl`} />
                      
                      </div>
                      <div>
                        <div className="text-gray-900 font-semibold text-sm ">{item.label}</div>
                        <div className="text-gray-500 text-xs mt-0.5  whitespace-pre-line">{item.value}</div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* ISO Badges */}
                <div className="flex flex-col gap-3 mb-8">
                  <div className="flex items-center gap-3 bg-teal-50 border border-teal-200 rounded-xl px-4 py-3">
                    <i className="ri-award-fill text-teal-600 text-xl" />
                    <div>
                      <div className="text-xs text-teal-600 font-semibold">Quality Management</div>
                      <div className="text-sm font-bold text-gray-900">ISO 9001:2015 Certified</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 bg-teal-50 border border-teal-200 rounded-xl px-4 py-3">
                    <i className="ri-shield-check-fill text-teal-600 text-xl" />
                    <div>
                      <div className="text-xs text-teal-600 font-semibold">Information Security</div>
                      <div className="text-sm font-bold text-gray-900">ISO/IEC 27001:2022 Certified</div>
                    </div>
                  </div>
                </div>

                {/* Why Us */}
                <div className="space-y-3">
                  {[
                    { icon: 'ri-calendar-check-line', title: 'Free Initial Consultation', desc: 'No-obligation discovery call with our architects.' },
                    { icon: 'ri-draft-line', title: 'Custom Solution Design', desc: 'Tailored proposals built for your use case.' },
                    { icon: 'ri-customer-service-2-line', title: '24/7 Support', desc: 'Dedicated post-launch support packages.' },
                  ].map((item) => (
                    <div key={item.title} className="flex items-start gap-3">
                      <div className="w-9 h-9 flex items-center justify-center bg-teal-100 text-teal-600 rounded-xl flex-shrink-0">
                        <i className={`${item.icon} text-lg`} />
                      </div>
                      <div>
                        <div className="text-gray-900 font-semibold text-sm">{item.title}</div>
                        <div className="text-gray-400 text-xs mt-0.5">{item.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right Form */}
              <div className={`lg:col-span-3 transition-all duration-700 delay-300 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                <div className="bg-gray-50 border border-gray-100 rounded-2xl p-8 lg:p-10">
                  <h3 className="text-gray-900 text-2xl font-bold mb-7">Request a Consultation</h3>

                  {status === 'success' ? (
                    <div className="text-center py-16">
                      <div className="w-16 h-16 flex items-center justify-center bg-teal-100 text-teal-600 rounded-full mx-auto mb-5">
                        <i className="ri-check-double-line text-3xl" />
                      </div>
                      <h4 className="text-gray-900 text-xl font-bold mb-2">Inquiry Sent Successfully!</h4>
                      <p className="text-gray-500">Our team will reach out within 24 hours. We look forward to working with you.</p>
                      <button
                        onClick={() => setStatus('idle')}
                        className="cursor-pointer whitespace-nowrap mt-6 bg-teal-500 hover:bg-teal-600 text-white font-semibold px-6 py-3 rounded-lg transition-colors"
                      >
                        Send Another Inquiry
                      </button>
                    </div>
                  ) : (
                    <form data-readdy-form onSubmit={handleSubmit} className="space-y-5">
                      <div className="grid sm:grid-cols-2 gap-5">
                        <div>
                          <label className="block text-gray-600 text-xs font-medium mb-2" htmlFor="fullname">Full Name *</label>
                          <input id="fullname" name="fullname" type="text" required placeholder="John Doe"
                            className="w-full bg-white border border-gray-200 text-gray-900 text-sm placeholder-gray-400 rounded-xl px-4 py-3 focus:outline-none focus:border-teal-400 transition-colors" />
                        </div>
                        <div>
                          <label className="block text-gray-600 text-xs font-medium mb-2" htmlFor="email">Email Address *</label>
                          <input id="email" name="email" type="email" required placeholder="john@company.com"
                            className="w-full bg-white border border-gray-200 text-gray-900 text-sm placeholder-gray-400 rounded-xl px-4 py-3 focus:outline-none focus:border-teal-400 transition-colors" />
                        </div>
                      </div>
                      <div className="grid sm:grid-cols-2 gap-5">
                        <div>
                          <label className="block text-gray-600 text-xs font-medium mb-2" htmlFor="phone">Phone Number</label>
                          <input id="phone" name="phone" type="tel" placeholder="+1 (555) 000-0000"
                            className="w-full bg-white border border-gray-200 text-gray-900 text-sm placeholder-gray-400 rounded-xl px-4 py-3 focus:outline-none focus:border-teal-400 transition-colors" />
                        </div>
                        <div>
                          <label className="block text-gray-600 text-xs font-medium mb-2" htmlFor="company">Company Name</label>
                          <input id="company" name="company" type="text" placeholder="Your Company Ltd."
                            className="w-full bg-white border border-gray-200 text-gray-900 text-sm placeholder-gray-400 rounded-xl px-4 py-3 focus:outline-none focus:border-teal-400 transition-colors" />
                        </div>
                      </div>
                      <div>
                        <label className="block text-gray-600 text-xs font-medium mb-2" htmlFor="service">Service Interest *</label>
                        <select id="service" name="service" required
                          className="w-full bg-white border border-gray-200 text-gray-900 text-sm rounded-xl px-4 py-3 focus:outline-none focus:border-teal-400 transition-colors cursor-pointer">
                          <option value="">Select a service...</option>
                          <option value="AI Solutions">AI Solutions</option>
                          <option value="Web Application">Web Application</option>
                          <option value="Windows Application">Windows Application</option>
                          <option value="Mobile Application">Mobile Application</option>
                          <option value="Magic Designing Software">Magic Designing Software</option>
                          <option value="Photo Press & Ebook">Photo Press &amp; Ebook</option>
                          <option value="Ship Chandling System">Ship Chandling Quotation System</option>
                          <option value="Inventory Management">Inventory Management System</option>
                          <option value="POS Solution">POS Solution</option>
                          <option value="RFID Based Solution">RFID Based Solution</option>
                          <option value="RFID Library Management">RFID Library Management System</option>
                          <option value="Custom Solution">Custom Solution</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-gray-600 text-xs font-medium mb-2" htmlFor="budget">Project Budget Range</label>
                        <select id="budget" name="budget"
                          className="w-full bg-white border border-gray-200 text-gray-900 text-sm rounded-xl px-4 py-3 focus:outline-none focus:border-teal-400 transition-colors cursor-pointer">
                          <option value="">Select budget range...</option>
                          <option value="Under $5,000">Under $5,000</option>
                          <option value="$5,000 - $15,000">$5,000 – $15,000</option>
                          <option value="$15,000 - $50,000">$15,000 – $50,000</option>
                          <option value="$50,000 - $100,000">$50,000 – $100,000</option>
                          <option value="Over $100,000">Over $100,000</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-gray-600 text-xs font-medium mb-2" htmlFor="message">
                          Project Requirements <span className="text-gray-400">({charCount}/500)</span>
                        </label>
                        <textarea id="message" name="message" rows={4} maxLength={500}
                          placeholder="Tell us about your project goals, challenges, and timeline..."
                          onChange={(e) => setCharCount(e.target.value.length)}
                          className="w-full bg-white border border-gray-200 text-gray-900 text-sm placeholder-gray-400 rounded-xl px-4 py-3 focus:outline-none focus:border-teal-400 transition-colors resize-none" />
                      </div>
                      {status === 'error' && (
                        <div className="bg-red-50 border border-red-200 text-red-600 text-sm rounded-xl px-4 py-3">
                          Something went wrong. Please try again or call us directly.
                        </div>
                      )}
                      <button type="submit" disabled={status === 'sending'}
                        className="cursor-pointer whitespace-nowrap w-full bg-teal-500 hover:bg-teal-600 disabled:opacity-60 text-white font-semibold py-4 rounded-xl transition-all flex items-center justify-center gap-2">
                        {status === 'sending' ? (
                          <><i className="ri-loader-4-line animate-spin" /> Sending...</>
                        ) : (
                          <><i className="ri-send-plane-line" /> Submit Inquiry</>
                        )}
                      </button>
                      <p className="text-center text-gray-400 text-xs">
                        We&apos;ll respond within 24 hours. &nbsp;
                        <Link to="/privacy-policy" className="text-teal-500 hover:underline cursor-pointer">Privacy Policy</Link>
                      </p>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Map Placeholder */}
        <section className="pb-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="rounded-2xl overflow-hidden border border-gray-100 h-72">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3153.0197316905595!2d-122.4194!3d37.7749!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzfCsDQ2JzI5LjciTiAxMjLCsDI1JzA5LjgiVw!5e0!3m2!1sen!2sus!4v1"

                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Office Location"
              />
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
